import { useMemo } from 'react';
import { Select, SelectOption } from './Select';
import { COUNTRIES, flagEmoji } from '@/lib/iso/countries';

interface CountrySelectProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  disabled?: boolean;
  // Allow an empty option ("All countries" / "Any") at the top of the
  // list with this value (typically the empty string). Useful for
  // filter selects where "unset" is meaningful.
  allowEmpty?: { value: string; label: string };
  width?: 'auto' | 'full';
  className?: string;
  ariaLabel?: string;
}

export function CountrySelect({
  value,
  onChange,
  placeholder,
  disabled,
  allowEmpty,
  width = 'full',
  className,
  ariaLabel,
}: CountrySelectProps) {
  const options: SelectOption[] = useMemo(() => {
    const base: SelectOption[] = COUNTRIES.map((c) => ({
      value: c.code,
      label: c.name,
      hint: c.code,
      prefix: (
        <span aria-hidden role="img" className="text-base leading-none">
          {flagEmoji(c.code)}
        </span>
      ),
      searchAliases: [c.code, ...(c.aliases ?? [])],
    }));
    if (allowEmpty) {
      return [{ value: allowEmpty.value, label: allowEmpty.label }, ...base];
    }
    return base;
  }, [allowEmpty]);

  return (
    <Select
      value={value ? value.toUpperCase() : value}
      onChange={(v) => onChange(v ? v.toUpperCase() : v)}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      width={width}
      className={className}
      ariaLabel={ariaLabel}
    />
  );
}
